import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageSEO from "@/components/PageSEO";

const TODAY = new Date().toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });

export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col" data-testid="page-terms">
      <PageSEO
        title="Condiciones de Uso"
        description="Condiciones de uso del sitio puravidatuxpan.com — reservas, pagos, políticas de cancelación y responsabilidad para hospedaje en Villas Pura Vida, Tuxpan Veracruz."
        path="/condiciones-de-uso"
      />
      <Header />
      <main className="flex-1 max-w-3xl w-full mx-auto px-6 md:px-10 py-20 md:py-28">
        <p className="text-eyebrow text-[#E07A5F] mb-4">Legal</p>
        <h1 className="font-serif-display text-5xl md:text-6xl leading-none mb-6">Condiciones de Uso</h1>
        <p className="text-stone-500 text-sm mb-12">Última actualización: {TODAY}</p>

        <article className="prose prose-stone max-w-none space-y-8 text-stone-700 leading-relaxed">

          <Section title="1. Aceptación">
            <p>Al acceder, navegar o realizar una reservación en <a href="https://puravidatuxpan.com" className="text-[#E07A5F] underline">puravidatuxpan.com</a> (en adelante "el sitio"), aceptas estas Condiciones de Uso y nuestro <Link to="/aviso-de-privacidad" className="text-[#E07A5F] underline">Aviso de Privacidad</Link>. Si no estás de acuerdo, te pedimos no utilizar el sitio.</p>
          </Section>

          <Section title="2. Sobre Villas Pura Vida">
            <p>Villas Pura Vida es un servicio de alojamiento turístico ubicado en Playa Norte, Barra de Galindo, Tuxpan, Veracruz, México, que ofrece la renta directa de tres alojamientos:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><b>La Casita Pura Vida</b> — capacidad hasta 14 huéspedes.</li>
              <li><b>Loft Pura Brisa</b> — capacidad hasta 6 huéspedes.</li>
              <li><b>Mini Loft Pura Vida</b> — capacidad hasta 6 huéspedes.</li>
            </ul>
          </Section>

          <Section title="3. Reservaciones y pagos">
            <p>Para reservar debes seleccionar fechas, número de huéspedes y completar el pago a través de la plataforma <b>Stripe</b>. Las reservas confirmadas con pago exitoso son <b>vinculantes</b> y se rigen por estas condiciones.</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Los precios se muestran en pesos mexicanos (MXN) e incluyen el IVA cuando aplica.</li>
              <li>El sitio ofrece un descuento de <b>10% sobre el precio de Airbnb</b> al reservar directo.</li>
              <li>Los códigos promocionales y descuentos del quiz / animalitos interactivos son válidos únicamente durante el tiempo que indique el sitio.</li>
              <li>Al confirmar tu reserva recibirás un correo de confirmación con el comprobante y una guía de llegada en PDF.</li>
            </ul>
          </Section>

          <Section title="4. Depósito reembolsable">
            <p>Al momento del check-in se solicitará un <b>depósito reembolsable de $1,000 MXN</b> en efectivo o transferencia, como garantía. Será devuelto íntegramente <b>en un plazo máximo de 24 horas posteriores a tu salida</b>, siempre que la propiedad sea entregada en las mismas condiciones en que fue recibida y no haya daños ni faltantes.</p>
          </Section>

          <Section title="5. Política de cancelación">
            <p>Salvo acuerdo distinto por escrito vía WhatsApp o correo:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><b>Cancelación con más de 30 días de anticipación:</b> reembolso del 100% menos comisiones de procesamiento (~3.6%).</li>
              <li><b>Entre 15 y 30 días:</b> reembolso del 50%.</li>
              <li><b>Menos de 15 días:</b> sin reembolso.</li>
              <li><b>No-show o cancelación el día de llegada:</b> sin reembolso.</li>
            </ul>
            <p>En caso de fuerza mayor (huracán, alerta gubernamental, cierre de carreteras), trabajaremos contigo para reprogramar tu estancia o emitir crédito por el monto pagado.</p>
          </Section>

          <Section title="6. Check-in y check-out">
            <ul className="list-disc pl-6 space-y-1">
              <li><b>Check-in:</b> 3:00 PM.</li>
              <li><b>Check-out:</b> 12:00 PM (medio día).</li>
              <li>El acceso a la propiedad es por playa; recomendamos llegar entre 2:00 y 5:00 PM por temas de marea.</li>
              <li>Salidas posteriores al horario podrán generar cargo adicional equivalente a una noche.</li>
            </ul>
          </Section>

          <Section title="7. Reglas de la casa">
            <p>Para conservar la propiedad, el entorno natural y la tranquilidad de la zona, las reservas implican aceptación de las siguientes reglas:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Capacidad máxima estricta: no se permiten huéspedes adicionales sin autorización previa.</li>
              <li><b>No se permite música fuerte después de las 10:00 PM.</b> Es zona residencial de playa.</li>
              <li>No se permite fumar dentro de la propiedad.</li>
              <li>Mascotas únicamente con autorización previa por escrito.</li>
              <li>No se permiten fiestas, eventos masivos ni el uso comercial de la propiedad sin autorización.</li>
              <li>El huésped es responsable del cuidado de la fauna local (cangrejos, tortugas, aves) y de retirar su basura.</li>
              <li>Daños, faltantes o sanciones por incumplimiento serán descontados del depósito o cobrados por separado.</li>
            </ul>
          </Section>

          <Section title="8. Servicios y variaciones naturales">
            <p>La propiedad cuenta con energía eléctrica, agua e internet WiFi. Sin embargo, debido a las características naturales de la zona costera de Barra de Galindo, <b>pueden presentarse variaciones ocasionales</b> en estos servicios (cortes de luz, intermitencias de señal celular o WiFi). Estaremos atentos para apoyarte ante cualquier eventualidad pero no nos hacemos responsables por interrupciones derivadas de fuerza mayor o causas ajenas a nuestro control directo.</p>
          </Section>

          <Section title="9. Responsabilidad del huésped">
            <p>El huésped reconoce que:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>El acceso es por camino de playa: debe llegar con luz de día y respetando los horarios sugeridos para evitar problemas de marea.</li>
              <li>Es responsable de su seguridad y la de sus acompañantes dentro y fuera de la propiedad.</li>
              <li>Debe traer sus propios alimentos, bebidas, repelente de mosquitos y artículos personales para toda la estancia.</li>
              <li>Carga combustible en Tuxpan antes de salir; no hay gasolineras cerca de la playa.</li>
              <li>Acepta que actividades de playa, mar y naturaleza implican riesgos inherentes y libera a Villas Pura Vida de cualquier responsabilidad por incidentes derivados de su propia conducta.</li>
            </ul>
          </Section>

          <Section title="10. Propiedad intelectual">
            <p>Todos los contenidos del sitio (textos, fotografías, ilustraciones, código, diseño, marca "Villas Pura Vida") son propiedad de Villas Pura Vida o cuentan con licencia para su uso. Queda prohibida su reproducción total o parcial sin autorización previa por escrito.</p>
          </Section>

          <Section title="11. Limitación de responsabilidad">
            <p>En la máxima medida permitida por la ley, Villas Pura Vida no será responsable por daños indirectos, incidentales o consecuentes derivados del uso del sitio o de la estancia. La responsabilidad total acumulada estará siempre limitada al monto efectivamente pagado por la reservación correspondiente.</p>
          </Section>

          <Section title="12. Modificaciones">
            <p>Podemos actualizar estas Condiciones en cualquier momento, publicando la nueva versión en esta misma página. Las reservas realizadas antes de una modificación se rigen por las condiciones vigentes en el momento de la compra.</p>
          </Section>

          <Section title="13. Ley aplicable y jurisdicción">
            <p>Estas Condiciones de Uso se rigen por las leyes de los Estados Unidos Mexicanos. Cualquier controversia será sometida a la jurisdicción de los tribunales competentes de Tuxpan, Veracruz, México, renunciando expresamente a cualquier otro fuero que pudiera corresponder.</p>
          </Section>

          <Section title="14. Contacto">
            <p>Para dudas o aclaraciones:<br/>
            Correo: <a href="mailto:reservas@puravidatuxpan.com" className="text-[#E07A5F] underline">reservas@puravidatuxpan.com</a><br/>
            WhatsApp: <a href="https://wa.me/5217711290073" target="_blank" rel="noopener" className="text-[#E07A5F] underline">+521 7711290073</a></p>
          </Section>

        </article>

        <div className="mt-16 text-center">
          <Link to="/" className="text-eyebrow text-[#E07A5F] hover:underline">← Volver al inicio</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-serif-display text-2xl text-stone-900 mb-4">{title}</h2>
      <div className="space-y-3 text-base">{children}</div>
    </section>
  );
}
